import EndpointCard from '../EndpointCard';
import ParamTable from '../ParamTable';
import CodeBlock from '../CodeBlock';

const SETTLEMENT_DETAILS = [
  ['Network', 'Polygon (MATIC)'],
  ['Token', 'USDC (0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359)'],
  ['Split', '99% to merchant wallet, 1% platform fee to BonesPay treasury'],
  ['Settlement time', 'On-chain finality, typically under 5 seconds'],
  ['Verification', 'Every payment verifiable on Polygonscan via /api/verify/:id'],
];

export default function ReferenceSection() {
  return (
    <>
      <div className="mt-16 mb-8">
        <p className="font-mono text-[10px] text-bone-white/30 uppercase tracking-widest">Verification</p>
      </div>

      <EndpointCard
        id="verify"
        method="GET"
        path="/api/verify/:id"
        description="Public payment proof endpoint. Returns on-chain verification data including the Polygonscan transaction link."
      />
      <CodeBlock
        language="json"
        code={`{
  "order_uuid": "d290f1ee-6c54-4b01-90e6-d701748f0851",
  "amount_expected": 49.99,
  "amount_verified": 49.49,
  "platform_fee": 0.50,
  "merchant_txid": "0xabc123...",
  "polygonscan_url": "https://polygonscan.com/tx/0xabc123...",
  "provider": "moonpay_commerce",
  "verified_at": "2026-03-18T12:05:00.000Z",
  "verification_method": "provider_webhook",
  "status": "verified"
}`}
      />
      <p className="font-sans text-xs text-bone-white/50 mt-2 leading-relaxed">
        For pending or failed orders, amount_verified, merchant_txid, polygonscan_url, and
        verified_at will be null.
      </p>

      <div id="webhooks" className="scroll-mt-20 mt-12 mb-8">
        <p className="font-mono text-[10px] text-bone-white/30 uppercase tracking-widest">Webhooks</p>
      </div>

      <EndpointCard
        method="POST"
        path="/api/webhooks/moonpay"
        description="Internal endpoint that receives signed payment lifecycle events from MoonPay Commerce. You do not call this endpoint -- MoonPay does."
      />
      <p className="font-sans text-sm text-bone-white/60 leading-relaxed mt-4">
        When a payment completes, BonesPay updates the order and fires an HMAC-SHA256 signed
        callback to your store's callback_url. Verify the signature using your webhook_secret
        before trusting the payload.
      </p>

      <section id="integration" className="scroll-mt-20 mt-16 mb-16">
        <h2 className="font-serif text-bone-white text-2xl mb-2">Integration Guide</h2>
        <div className="w-12 h-px bg-bone-white/20 mb-6" />
        <p className="font-sans text-sm text-bone-white/50 mb-8">WooCommerce Example</p>

        <div className="space-y-10">
          <div>
            <h3 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
              1. Register your merchant account
            </h3>
            <CodeBlock
              language="bash"
              code={`curl -X POST https://api.bonespay.com/api/merchants/register \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "you@yourstore.com",
    "business_name": "Your Store",
    "default_wallet": "0xYourPolygonWalletAddress",
    "webhook_url": "https://yourstore.com/wc-api/bonespay"
  }'`}
            />
            <p className="font-sans text-xs text-bone-white/50 mt-2">
              Save the api_key from the response. You will not see it again.
            </p>
          </div>

          <div>
            <h3 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
              2. Create a checkout when a customer places an order
            </h3>
            <CodeBlock
              language="bash"
              code={`curl -X POST https://api.bonespay.com/api/checkout/create \\
  -H "Content-Type: application/json" \\
  -H "X-Bonespay-Key: your_api_key" \\
  -d '{
    "merchant_wallet": "0xYourPolygonWalletAddress",
    "order_amount_usd": 49.99,
    "order_currency": "EUR",
    "order_original_amount": 45.50,
    "external_order_id": "WC-1234"
  }'`}
            />
          </div>

          <div>
            <h3 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
              3. Redirect the customer
            </h3>
            <p className="font-sans text-sm text-bone-white/60 leading-relaxed">
              Take the checkout_url from the response and redirect the customer to it. They will
              complete payment through MoonPay's hosted checkout page.
            </p>
          </div>

          <div>
            <h3 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
              4. Receive the webhook callback
            </h3>
            <p className="font-sans text-sm text-bone-white/60 leading-relaxed">
              When the customer pays, BonesPay sends an HMAC-SHA256 signed POST request to your
              callback_url. Verify the signature with your webhook_secret, then mark the WooCommerce
              order as paid.
            </p>
          </div>

          <div>
            <h3 className="font-mono text-xs text-bone-white uppercase tracking-wider mb-3">
              5. Verify on-chain (optional)
            </h3>
            <CodeBlock language="bash" code="curl https://api.bonespay.com/api/verify/ORDER_UUID" />
            <p className="font-sans text-xs text-bone-white/50 mt-2">
              Returns the Polygonscan transaction link for full on-chain proof of payment.
            </p>
          </div>
        </div>
      </section>

      <section id="errors" className="scroll-mt-20 mb-16">
        <h2 className="font-serif text-bone-white text-2xl mb-2">Errors</h2>
        <div className="w-12 h-px bg-bone-white/20 mb-6" />
        <p className="font-sans text-sm text-bone-white/60 mb-4 leading-relaxed">
          All error responses follow this format:
        </p>
        <CodeBlock language="json" code={`{ "error": "Human-readable error message" }`} />
        <ParamTable
          params={[
            { field: '400', type: 'status', description: 'Bad request -- invalid parameters or body' },
            { field: '401', type: 'status', description: 'Unauthorized -- missing or invalid API key / webhook signature' },
            { field: '403', type: 'status', description: 'Forbidden -- wallet restricted (OFAC screening)' },
            { field: '404', type: 'status', description: 'Not found -- order or resource does not exist' },
            { field: '409', type: 'status', description: 'Conflict -- resource already exists (e.g. duplicate email)' },
            { field: '500', type: 'status', description: 'Internal server error' },
            { field: '502', type: 'status', description: 'Bad gateway -- upstream provider unavailable' },
          ]}
        />
      </section>

      <section id="rate-limits" className="scroll-mt-20 mb-16">
        <h2 className="font-serif text-bone-white text-2xl mb-2">Rate Limits</h2>
        <div className="w-12 h-px bg-bone-white/20 mb-6" />
        <p className="font-sans text-sm text-bone-white/60 leading-relaxed">
          The API uses KV-backed rate limiting. If you hit a rate limit, you will receive a{' '}
          <code className="font-mono text-xs text-bone-white bg-navy-900 px-1.5 py-0.5 rounded-subtle">
            429 Too Many Requests
          </code>{' '}
          response. Back off and retry with exponential backoff.
        </p>
      </section>

      <section id="settlement" className="scroll-mt-20 mb-8">
        <h2 className="font-serif text-bone-white text-2xl mb-2">Settlement Details</h2>
        <div className="w-12 h-px bg-bone-white/20 mb-6" />
        <ul className="space-y-3 list-none">
          {SETTLEMENT_DETAILS.map(([label, value]) => (
            <li key={label} className="flex gap-4">
              <span className="font-mono text-xs text-bone-white/40 w-36 flex-shrink-0">{label}</span>
              <span className="font-sans text-sm text-bone-white/60">{value}</span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
